/**
 * TypeScript types for Anvil Protocol contracts
 */

export type Address = `0x${string}`

export enum LOCStatus {
  Active = 0,
  Redeemed = 1,
  Expired = 2,
  Cancelled = 3,
}

export interface Vault {
  id: bigint
  owner: Address
  collateral: CollateralAsset[]
  totalValueUSD: bigint
  healthRatio: number
  createdAt: number
}

export interface CollateralAsset {
  address: Address
  symbol: string
  name: string
  amount: bigint
  decimals: number
  priceUSD: bigint
  valueUSD: bigint
}

export interface LetterOfCredit {
  id: bigint
  vaultId: bigint
  creator: Address
  beneficiary: Address
  creditedAmount: bigint
  creditedAsset: Address
  creditedAssetSymbol: string
  expiry: bigint
  status: LOCStatus
  createdAt: number
  isActive: boolean
}

export interface PriceData {
  asset: Address
  price: bigint
  decimals: number
  timestamp: number
}

// Transaction types
export interface DepositParams {
  vaultId: bigint
  asset: Address
  amount: bigint
}

export interface WithdrawParams {
  vaultId: bigint
  asset: Address
  amount: bigint
}

export interface IssueLOCParams {
  vaultId: bigint
  beneficiary: Address
  creditedAmount: bigint
  creditedAsset: Address
  expiry: bigint
}

export interface RedeemLOCParams {
  locId: bigint
  recipient: Address
}

export interface ExtendLOCParams {
  locId: bigint
  newExpiry: bigint
}

// Supported collateral assets
export const SUPPORTED_ASSETS = {
  WETH: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
  USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  cbBTC: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf',
  DAI: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
} as const

export type SupportedAssetSymbol = keyof typeof SUPPORTED_ASSETS

export interface AssetInfo {
  address: Address
  symbol: SupportedAssetSymbol
  name: string
  decimals: number
  icon?: string
}

export const ASSET_INFO: Record<SupportedAssetSymbol, AssetInfo> = {
  WETH: {
    address: SUPPORTED_ASSETS.WETH,
    symbol: 'WETH',
    name: 'Wrapped Ether',
    decimals: 18,
  },
  USDC: {
    address: SUPPORTED_ASSETS.USDC,
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
  },
  USDT: {
    address: SUPPORTED_ASSETS.USDT,
    symbol: 'USDT',
    name: 'Tether USD',
    decimals: 6,
  },
  cbBTC: {
    address: SUPPORTED_ASSETS.cbBTC,
    symbol: 'cbBTC',
    name: 'Coinbase Wrapped BTC',
    decimals: 8,
  },
  DAI: {
    address: SUPPORTED_ASSETS.DAI,
    symbol: 'DAI',
    name: 'Dai Stablecoin',
    decimals: 18,
  },
}

// Health ratio thresholds
export const HEALTH_RATIO_THRESHOLDS = {
  CRITICAL: 1.1,
  WARNING: 1.3,
  HEALTHY: 1.5,
} as const

// Collateral ratio requirements
export const MIN_COLLATERAL_RATIO = 1.5 // 150%
export const LIQUIDATION_RATIO = 1.2 // 120%

